import { NavLink, Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { can as hasPermission, useSession } from '@/shared/auth/session'

/**
 * Axis Advisory — the module shell.
 *
 * ## Two doors, and the tab order says which is which
 *
 * **Brief** is what arrives — the pinned executive brief, written overnight and
 * waiting. **Console** is what you ask for — a playbook against a subject.
 * **Analyst** is what you ask about — a conversation, where every turn is still a
 * full audited run. Those three lead, because they are the three ways somebody
 * actually enters this module.
 *
 * Everything after them is the machinery: where the answers came from, what they
 * want done, and what it all costs.
 *
 * ## Governance tabs are not hidden from people who cannot use them
 *
 * A tab the caller lacks the authority for is filtered out, following the rest of
 * the app. What is NOT filtered is the Usage tab: knowing what the platform is
 * spending on models is part of `advisor:read`, because a console whose cost is
 * only visible to whoever can change it is a console nobody questions.
 */

interface Tab {
  to: string
  label: string
  hint: string
  permission?: string
  end?: boolean
}

interface Group {
  label: string
  tabs: Tab[]
}

export const ADVISORY_GROUPS: Group[] = [
  {
    label: 'Ask',
    tabs: [
      {
        to: '/advisory',
        label: 'Brief',
        hint: 'What changed, what needs a decision today',
        permission: 'advisor:read',
        end: true,
      },
      {
        to: '/advisory/console',
        label: 'Console',
        hint: 'Run a playbook against a subject',
        permission: 'advisor:read',
      },
      {
        to: '/advisory/analyst',
        label: 'Analyst',
        hint: 'Ask a question about this platform’s own data',
        permission: 'advisor:read',
      },
      {
        to: '/advisory/map',
        label: 'Map',
        hint: 'Score every unit in a scope and paint the result',
        permission: 'advisor:read',
      },
    ],
  },
  {
    label: 'Output',
    tabs: [
      {
        to: '/advisory/findings',
        label: 'Findings',
        hint: 'Every finding, newest first',
        permission: 'advisor:read',
      },
      {
        to: '/advisory/proposals',
        label: 'Proposals',
        hint: 'Approve, then apply — two gates, two people',
        permission: 'advisor:read',
      },
      {
        to: '/advisory/studio',
        label: 'Studio',
        hint: 'Generated assets, permanently marked as generated',
        permission: 'advisor:read',
      },
    ],
  },
  {
    label: 'Machinery',
    tabs: [
      {
        to: '/advisory/runs',
        label: 'Runs',
        hint: 'Every council that has run, and what each model said',
        permission: 'advisor:read',
      },
      {
        to: '/advisory/playbooks',
        label: 'Playbooks',
        hint: 'The governed prompts and the councils behind them',
        permission: 'advisor:read',
      },
      {
        to: '/advisory/models',
        label: 'Models',
        hint: 'Which models this deployment may use, and whether they are real',
        permission: 'advisor:read',
      },
      {
        to: '/advisory/usage',
        label: 'Usage',
        hint: 'Tokens, cost, budget and scheduled briefings',
        permission: 'advisor:read',
      },
    ],
  },
]

export function AdvisoryLayout() {
  const authorities = useSession((s) => s.authorities)

  const groups = ADVISORY_GROUPS.map((g) => ({
    ...g,
    tabs: g.tabs.filter((t) => hasPermission(authorities, t.permission)),
  })).filter((g) => g.tabs.length > 0)

  return (
    <div className="space-y-4">
      <nav aria-label="Axis Advisory" className="axis-table-scroll border-b border-border">
        <ul className="flex items-stretch gap-4 min-w-max">
          {groups.map((group) => (
            <li key={group.label} className="flex items-stretch gap-1">
              <span aria-hidden
                className="self-center pr-1 text-erp-xs uppercase tracking-wide
                  text-muted-foreground/70">
                {group.label}
              </span>
              {group.tabs.map((tab) => (
                <NavLink key={tab.to} to={tab.to} end={tab.end} title={tab.hint}
                  className={({ isActive }) => cn(
                    'inline-block whitespace-nowrap px-3 py-2 text-erp-xs font-medium',
                    'border-b-2 -mb-px transition-colors',
                    isActive
                      ? 'border-chocolate text-chocolate'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}>
                  {tab.label}
                </NavLink>
              ))}
            </li>
          ))}
        </ul>
      </nav>

      <Outlet />
    </div>
  )
}
